import { postalCodeDigits, type ApiAddress, type EnumCountry } from '@Model';
import { TextInput, useTranslate, type IGuiBinder } from '@synqo/frontend-component-library';
import { useEffect, type KeyboardEvent, type RefObject } from 'react';

// @ai-generated 2026-08-19T14:09:15+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

/** Digits only, truncated to field length — catches pastes that bypass the key guard. */
function onlyDigits(value: string, maxLength: number): string {
    return value.replace(/\D/g, '').slice(0, maxLength);
}

/** Keys the digit guard lets through: everything that edits rather than inserts a character. */
function isEditingKey(event: KeyboardEvent<HTMLInputElement>): boolean {
    return (
        event.key.length > 1 || // Backspace, Delete, Tab, arrows, Enter, …
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
    );
}

export interface AddressPostalCodeInputProps {
    binder: IGuiBinder<ApiAddress>;
    country: EnumCountry;
    /** Reports what the field shows. */
    onZipTyped: (zip: string) => void;
    /**
     * The automation's handle on this field. It reads the cursor off it: an answer that arrives
     * while the user is still here is the one that may move them on to the city field.
     */
    inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | undefined>;
}

/**
 * Postal code field. In countries with purely numeric postal codes it is limited to digits and to
 * the length of the country's code, elsewhere it stays freely editable — an alphanumeric code
 * (NL "1234 AB", GB "SW1A 1AA") must never be blocked or truncated.
 *
 * An ordinary field, and deliberately so: what the postal code means for the city is answered at
 * the city field, in one place, and never in two. The only thing that ever leaves here is the
 * cursor, when the answer turns out to hold a choice — see `useLocalityHandover`.
 */
export function AddressPostalCodeInput({ binder, country, onZipTyped, inputRef }: AddressPostalCodeInputProps) {
    const i18n = useTranslate('');
    const digits = postalCodeDigits(country);

    // mapUserInput cleans on commit: the key guard only covers keyboard input and `maxLength` only
    // limits length. Without a digit country nothing is cleaned — otherwise an existing alphanumeric
    // postal code would lose its letters on mere tabbing.
    const zipProps = binder.bindToProp<string>(
        'zip_code',
        digits === undefined ? undefined : (value) => onlyDigits(value, digits),
    );
    const zipInRecord = zipProps.value ?? '';

    // Cleans a paste in the display right away. The commit sanitizes the record either way, but
    // until then the field would keep showing whatever was pasted, letters and all.
    useEffect(() => {
        const input = inputRef.current;
        if (input === undefined || digits === undefined) {
            return;
        }

        const onPaste = (event: Event) => {
            const pasted = (event as globalThis.ClipboardEvent).clipboardData?.getData('text') ?? '';
            const pastedDigits = pasted.replace(/\D/g, '');
            if (pastedDigits === pasted) {
                return;
            }

            const start = input.selectionStart ?? 0;
            const end = input.selectionEnd ?? 0;
            // `setRangeText` does not know `maxLength`: without capping it here the field would end
            // up holding more digits than the country's postal code is long.
            const room = digits - (input.value.length - (end - start));
            event.preventDefault();
            input.setRangeText(pastedDigits.slice(0, Math.max(room, 0)), start, end, 'end');
            input.dispatchEvent(new Event('input', { bubbles: true }));
        };

        input.addEventListener('paste', onPaste);
        return () => input.removeEventListener('paste', onPaste);
    }, [digits, inputRef]);

    return (
        <TextInput
            {...zipProps}
            inputRef={inputRef}
            onChange={(value, valid) => {
                onZipTyped(value);
                return zipProps.onChange(value, valid);
            }}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Escape') {
                    onZipTyped(zipInRecord);
                    return;
                }
                if (digits !== undefined && !isEditingKey(event) && !/^\d$/.test(event.key)) {
                    event.preventDefault();
                }
            }}
            maxLength={digits}
            label={i18n('Postleitzahl')}
        />
    );
}
